import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { getDossier } from "../../api/dossier/getDossier";
import { getDossierTermination } from "../../api/dossier/getDossierTermination";
import { getDossierTerminationStatus } from "../../api/dossier/getDossierTerminationStatus";
import AutoPilotAlert from "../../components/Alert";
import APDetailList from "../../components/APDetailList";
import APModal from "../../components/APModal";
import APContactHelpButton from "../../components/buttons/APContactHelpButton";
import BackButton from "../../components/buttons/BackButton";
import HomeButton from "../../components/buttons/HomeButton";
import InfoButton from "../../components/buttons/InfoButton";
import FaqList from "../../components/faq/FaqList";
import PageContainer from "../../components/PageContainer";
import DossierSummary from "../../employer/components/DossierSummary";
import DossierTerminationSummary from "../../employer/components/DossierTerminationSummary";
import { toDollars } from "../../helpers/currencies";
import { formatDate } from "../../helpers/dates";
import { useFaqs } from "../../hooks/useFaqs";
import { useAuth } from "../../providers/AuthProvider";
import { CRMAppLocationIdentifier } from "../../types/Faq";
import DossierStatusStepper from "../components/DossierStatusStepper";

const ViewDossierTermination = () => {
  const { dossierId } = useParams();
  const navigate = useNavigate();
  const [navigationCheckComplete, setNavigationCheck] = useState(false);
  const { getScopedPageUrl } = useAuth();
  const [showNoteToDriverModal, setShowNoteToDriverModal] = useState(false);

  if (!dossierId) {
    // TODO: improve client side error messaging
    throw new Error("dossier id param is misisng");
  }

  const dossier = getDossier(dossierId);
  const dossierTermination = getDossierTermination(dossierId, {
    disabled: !navigationCheckComplete,
  });
  const terminationStatus = getDossierTerminationStatus(dossierId, "Closing", {
    disabled: !navigationCheckComplete,
  });

  const {
    faqProps,
    isLoading: isLoadingFaqs,
    isSuccess: isSuccessFaqs,
  } = useFaqs({
    pageId: "driver-lease-closure",
    contextIds: {
      opportunityId: dossier?.data?.opportunity?.id,
    },
  });

  if (dossier.isError) {
    throw dossier.error;
  }

  if (dossierTermination.isError) {
    throw dossierTermination.error;
  }

  if (terminationStatus.isError) {
    throw terminationStatus.error;
  }

  const isLoading =
    dossier.isLoading ||
    dossierTermination.isLoading ||
    terminationStatus.isLoading ||
    isLoadingFaqs;
  const isSuccess =
    dossier.isSuccess &&
    dossierTermination.isSuccess &&
    terminationStatus.isSuccess &&
    isSuccessFaqs;

  useEffect(() => {
    if (dossier.isSuccess && !dossier.isFetching) {
      if (
        dossier.data?.terminationId &&
        dossier.data.closureStatus === "aware" &&
        ["Closing", "Closed"].includes(dossier.data.status)
      ) {
        setNavigationCheck(true);
      } else {
        navigate(getScopedPageUrl("dossierActivated", dossierId), {
          replace: true,
        });
      }
    }
  }, [dossier.isSuccess, dossier.isFetching]);

  const onStatusItemClick = () => {
    setShowNoteToDriverModal(true);
  };

  const onCloseNoteToDriverModal = () => {
    setShowNoteToDriverModal(false);
  };

  const renderAppLocation = faqProps.renderAppLocation;

  let pageTitle = "";

  if (dossier.data?.status === "Closing") {
    pageTitle = "Terminating";
  } else if (dossier.data?.status === "Closed") {
    pageTitle = "Terminated";
  }

  return (
    <PageContainer
      title={pageTitle}
      titleButton={
        isSuccess ? (
          <Box mr={1}>
            {renderAppLocation("DriverClose-Page", <InfoButton />, true, true)}
          </Box>
        ) : undefined
      }
      loading={isLoading}
    >
      {isSuccess && (
        <Stack spacing={2}>
          <DossierSummary
            dossier={dossier.data}
            displayedFields={["dossierIdentity", "startDate", "vehicle"]}
          />
          <DossierTerminationSummary
            dossierTermination={{
              leavingEmployer: dossierTermination.data.leavingEmployer,
              lastDateOfEmployment:
                dossierTermination.data.lastDateOfEmployment,
              terminationReason: dossierTermination.data.terminationReason,
              endDate: dossierTermination.data.endDate,
            }}
            displayedFields={[
              "leavingEmployer",
              "lastDateOfEmployment",
              "terminationReason",
              "endDate",
            ]}
            appLocations={{
              endDate: "DriverClose-EndDate",
            }}
            labelOverrides={{
              endDate: dossierTermination.data.terminationDateSet
                ? "End Date"
                : "Requested End Date",
            }}
            renderAppLocation={renderAppLocation}
          />

          <Box>
            <APDetailList
              boldLabels={false}
              items={[
                ...(dossierTermination.data.currentBalanceExGst !== null &&
                dossierTermination.data.cashBalanceFinalised
                  ? [
                      {
                        label: "Current balance ExGST",
                        value: toDollars(
                          dossierTermination.data.currentBalanceExGst,
                          2
                        ),
                        appLocationIdentifier:
                          "DriverClose-CurrentBalance" as CRMAppLocationIdentifier,
                      },
                    ]
                  : []),
                ...(dossierTermination.data.fuelCardTransactionsToBeLoaded
                  ? [
                      {
                        label: "Transactions to be loaded ExGST",
                        value: toDollars(
                          dossierTermination.data
                            .fuelCardTransactionsToBeLoaded,
                          2
                        ),
                        appLocationIdentifier:
                          "DriverClose-TransactionsToBeLoaded" as CRMAppLocationIdentifier,
                      },
                    ]
                  : []),
                ...(dossierTermination.data.projectedBalance !== null &&
                dossierTermination.data.cashBalanceFinalised
                  ? [
                      {
                        label: "Projected balance ExGST",
                        value: toDollars(
                          dossierTermination.data.projectedBalance,
                          2
                        ),
                        appLocationIdentifier:
                          "DriverClose-ProjectedBalance" as CRMAppLocationIdentifier,
                      },
                    ]
                  : []),
                ...(dossierTermination.data.nextFinancePaymentDue
                  ? [
                      {
                        label: "Next Finance Payment Due",
                        value: formatDate(
                          dossierTermination.data.nextFinancePaymentDue
                        ),
                        appLocationIdentifier:
                          "DriverClose-NextFinancePaymentDue" as CRMAppLocationIdentifier,
                      },
                    ]
                  : []),
                ...(dossierTermination.data.lastDateOfInsuranceCoverage
                  ? [
                      {
                        label: "Last Date of Insurance Cover",
                        value: formatDate(
                          dossierTermination.data.lastDateOfInsuranceCoverage
                        ),
                        appLocationIdentifier:
                          "DriverClose-LastDateInsuranceCover" as CRMAppLocationIdentifier,
                      },
                    ]
                  : []),
              ]}
              renderAppLocation={renderAppLocation}
            />
          </Box>

          <Box sx={{ pt: 1, pb: 2 }}>
            <Box sx={{ ml: 1 }}>
              <DossierStatusStepper
                dossierStatus={terminationStatus.data}
                onItemClick={
                  dossierTermination.data.noteToDriver
                    ? onStatusItemClick
                    : undefined
                }
              ></DossierStatusStepper>
            </Box>
            <APModal
              isOpen={showNoteToDriverModal}
              onClose={onCloseNoteToDriverModal}
            >
              <Box>
                <AutoPilotAlert
                  severity="info"
                  message={dossierTermination.data.noteToDriver ?? ""}
                  asHTML={true}
                />
              </Box>
            </APModal>
          </Box>

          {dossierTermination.data.noteToDriver && (
            <Box>
              <AutoPilotAlert
                severity="info"
                message={dossierTermination.data.noteToDriver}
                asHTML={true}
              />
            </Box>
          )}

          <FaqList
            pageId="driver-lease-closure"
            appLocationIdentifier="DriverClose-FAQList"
            faqContextIds={{
              opportunityId: dossier.data.opportunity?.id ?? undefined,
            }}
          />

          <Stack
            spacing={2}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              pt: 2,
            }}
          >
            <Box sx={{ display: "flex", columnGap: 1, alignItems: "center" }}>
              <BackButton />
              <HomeButton destination="dossierHome" dossierId={dossierId} />
            </Box>
            <APContactHelpButton
              regardingOpportunityId={dossier.data.opportunity?.id ?? undefined}
            />
          </Stack>
        </Stack>
      )}
    </PageContainer>
  );
};

export default ViewDossierTermination;
